/* eslint-disable */
import { Media } from './common.js';

import {
  LayoutData_ImagePosition,
  LayoutData_ResponsivenessBehaviour,
  LayoutData_VerticalAlignment_Alignment,
  LayoutData_ImageScaling_Scaling,
} from './node_layout.enums.js';

/** flynt-disable missing-string-validations */

export interface LayoutData {
  /** Background color as a hexadecimal value. */
  backgroundColor?: string;
  /** Background image. */
  backgroundImage?: LayoutData_BackgroundImage;
  /** Border color as a hexadecimal value. */
  borderColor?: string;
  /** Border width in pixels. */
  borderWidth?: number;
  /** Border */
  borderRadius?: number;
  /** Backdrop color as a hexadecimal value. */
  backdropColor?: string;
  /** Backdrop image.radius in pixels. */
  backdropImage?: LayoutData_BackgroundImage;
  /** Backdrop top padding. */
  backdropPaddingTop?: number;
  /** Backdrop bottom padding */
  backdropPaddingBottom?: number;
  /** Horizontal and vertical gap between columns */
  gap?: number;
  /** Padding in pixels for cells. Follows CSS order: top, right, bottom, left */
  cellPadding: number[];
  /** Vertical alignment for the cell's items. */
  cellVerticalAlignment: LayoutData_VerticalAlignment_Alignment;
  /** Responsiveness behaviour of columns when responsiveness applies. Either stacks or wrappers. */
  responsivenessBehaviour: LayoutData_ResponsivenessBehaviour;
  /** Size in pixels when responsiveness_behaviour applies */
  responsivenessBreakpoint?: number;
}

export function layoutData_ImagePositionFromJSON(object: any): LayoutData_ImagePosition {
  switch (object) {
    case 0:
    case 'CENTER':
      return LayoutData_ImagePosition.CENTER;
    case 1:
    case 'CENTER_LEFT':
      return LayoutData_ImagePosition.CENTER_LEFT;
    case 2:
    case 'CENTER_RIGHT':
      return LayoutData_ImagePosition.CENTER_RIGHT;
    case 3:
    case 'TOP':
      return LayoutData_ImagePosition.TOP;
    case 4:
    case 'TOP_LEFT':
      return LayoutData_ImagePosition.TOP_LEFT;
    case 5:
    case 'TOP_RIGHT':
      return LayoutData_ImagePosition.TOP_RIGHT;
    case 6:
    case 'BOTTOM':
      return LayoutData_ImagePosition.BOTTOM;
    case 7:
    case 'BOTTOM_LEFT':
      return LayoutData_ImagePosition.BOTTOM_LEFT;
    case 8:
    case 'BOTTOM_RIGHT':
      return LayoutData_ImagePosition.BOTTOM_RIGHT;
    case -1:
    case 'UNRECOGNIZED':
    default:
      return LayoutData_ImagePosition.UNRECOGNIZED;
  }
}

export function layoutData_ImagePositionToJSON(object: LayoutData_ImagePosition): string {
  switch (object) {
    case LayoutData_ImagePosition.CENTER:
      return 'CENTER';
    case LayoutData_ImagePosition.CENTER_LEFT:
      return 'CENTER_LEFT';
    case LayoutData_ImagePosition.CENTER_RIGHT:
      return 'CENTER_RIGHT';
    case LayoutData_ImagePosition.TOP:
      return 'TOP';
    case LayoutData_ImagePosition.TOP_LEFT:
      return 'TOP_LEFT';
    case LayoutData_ImagePosition.TOP_RIGHT:
      return 'TOP_RIGHT';
    case LayoutData_ImagePosition.BOTTOM:
      return 'BOTTOM';
    case LayoutData_ImagePosition.BOTTOM_LEFT:
      return 'BOTTOM_LEFT';
    case LayoutData_ImagePosition.BOTTOM_RIGHT:
      return 'BOTTOM_RIGHT';
    default:
      return 'UNKNOWN';
  }
}

export function layoutData_ResponsivenessBehaviourFromJSON(
  object: any
): LayoutData_ResponsivenessBehaviour {
  switch (object) {
    case 0:
    case 'STACK':
      return LayoutData_ResponsivenessBehaviour.STACK;
    case 1:
    case 'WRAP':
      return LayoutData_ResponsivenessBehaviour.WRAP;
    case -1:
    case 'UNRECOGNIZED':
    default:
      return LayoutData_ResponsivenessBehaviour.UNRECOGNIZED;
  }
}

export function layoutData_ResponsivenessBehaviourToJSON(
  object: LayoutData_ResponsivenessBehaviour
): string {
  switch (object) {
    case LayoutData_ResponsivenessBehaviour.STACK:
      return 'STACK';
    case LayoutData_ResponsivenessBehaviour.WRAP:
      return 'WRAP';
    default:
      return 'UNKNOWN';
  }
}

export interface LayoutData_VerticalAlignment {}

export function layoutData_VerticalAlignment_AlignmentFromJSON(
  object: any
): LayoutData_VerticalAlignment_Alignment {
  switch (object) {
    case 0:
    case 'TOP':
      return LayoutData_VerticalAlignment_Alignment.TOP;
    case 1:
    case 'MIDDLE':
      return LayoutData_VerticalAlignment_Alignment.MIDDLE;
    case 2:
    case 'BOTTOM':
      return LayoutData_VerticalAlignment_Alignment.BOTTOM;
    case -1:
    case 'UNRECOGNIZED':
    default:
      return LayoutData_VerticalAlignment_Alignment.UNRECOGNIZED;
  }
}

export function layoutData_VerticalAlignment_AlignmentToJSON(
  object: LayoutData_VerticalAlignment_Alignment
): string {
  switch (object) {
    case LayoutData_VerticalAlignment_Alignment.TOP:
      return 'TOP';
    case LayoutData_VerticalAlignment_Alignment.MIDDLE:
      return 'MIDDLE';
    case LayoutData_VerticalAlignment_Alignment.BOTTOM:
      return 'BOTTOM';
    default:
      return 'UNKNOWN';
  }
}

export interface LayoutData_ImageScaling {}

export function layoutData_ImageScaling_ScalingFromJSON(
  object: any
): LayoutData_ImageScaling_Scaling {
  switch (object) {
    case 0:
    case 'AUTO':
      return LayoutData_ImageScaling_Scaling.AUTO;
    case 1:
    case 'CONTAIN':
      return LayoutData_ImageScaling_Scaling.CONTAIN;
    case 2:
    case 'COVER':
      return LayoutData_ImageScaling_Scaling.COVER;
    case -1:
    case 'UNRECOGNIZED':
    default:
      return LayoutData_ImageScaling_Scaling.UNRECOGNIZED;
  }
}

export function layoutData_ImageScaling_ScalingToJSON(
  object: LayoutData_ImageScaling_Scaling
): string {
  switch (object) {
    case LayoutData_ImageScaling_Scaling.AUTO:
      return 'AUTO';
    case LayoutData_ImageScaling_Scaling.CONTAIN:
      return 'CONTAIN';
    case LayoutData_ImageScaling_Scaling.COVER:
      return 'COVER';
    default:
      return 'UNKNOWN';
  }
}

export interface LayoutData_BackgroundImage {
  /** Background image. */
  media?: Media;
  /** Background image opacity. */
  opacity?: number;
  /** Background image scaling. */
  scaling: LayoutData_ImageScaling_Scaling;
  /** Position of background. Defaults to `CENTER`. */
  position: LayoutData_ImagePosition;
}

const baseLayoutData: object = {
  cellPadding: 0,
  cellVerticalAlignment: LayoutData_VerticalAlignment_Alignment.TOP,
  responsivenessBehaviour: LayoutData_ResponsivenessBehaviour.STACK,
};

export const LayoutData = {
  fromJSON(object: any): LayoutData {
    const message = { ...baseLayoutData } as LayoutData;
    message.backgroundColor =
      object.backgroundColor !== undefined && object.backgroundColor !== null
        ? String(object.backgroundColor)
        : undefined;
    message.backgroundImage =
      object.backgroundImage !== undefined && object.backgroundImage !== null
        ? LayoutData_BackgroundImage.fromJSON(object.backgroundImage)
        : undefined;
    message.borderColor =
      object.borderColor !== undefined && object.borderColor !== null
        ? String(object.borderColor)
        : undefined;
    message.borderWidth =
      object.borderWidth !== undefined && object.borderWidth !== null
        ? Number(object.borderWidth)
        : undefined;
    message.borderRadius =
      object.borderRadius !== undefined && object.borderRadius !== null
        ? Number(object.borderRadius)
        : undefined;
    message.backdropColor =
      object.backdropColor !== undefined && object.backdropColor !== null
        ? String(object.backdropColor)
        : undefined;
    message.backdropImage =
      object.backdropImage !== undefined && object.backdropImage !== null
        ? LayoutData_BackgroundImage.fromJSON(object.backdropImage)
        : undefined;
    message.backdropPaddingTop =
      object.backdropPaddingTop !== undefined && object.backdropPaddingTop !== null
        ? Number(object.backdropPaddingTop)
        : undefined;
    message.backdropPaddingBottom =
      object.backdropPaddingBottom !== undefined && object.backdropPaddingBottom !== null
        ? Number(object.backdropPaddingBottom)
        : undefined;
    message.gap = object.gap !== undefined && object.gap !== null ? Number(object.gap) : undefined;
    message.cellPadding = (object.cellPadding ?? []).map((e: any) => Number(e));
    message.cellVerticalAlignment =
      object.cellVerticalAlignment !== undefined && object.cellVerticalAlignment !== null
        ? layoutData_VerticalAlignment_AlignmentFromJSON(object.cellVerticalAlignment)
        : LayoutData_VerticalAlignment_Alignment.TOP;
    message.responsivenessBehaviour =
      object.responsivenessBehaviour !== undefined && object.responsivenessBehaviour !== null
        ? layoutData_ResponsivenessBehaviourFromJSON(object.responsivenessBehaviour)
        : LayoutData_ResponsivenessBehaviour.STACK;
    message.responsivenessBreakpoint =
      object.responsivenessBreakpoint !== undefined && object.responsivenessBreakpoint !== null
        ? Number(object.responsivenessBreakpoint)
        : undefined;
    return message;
  },

  toJSON(message: LayoutData): unknown {
    const obj: any = {};
    message.backgroundColor !== undefined && (obj.backgroundColor = message.backgroundColor);
    message.backgroundImage !== undefined &&
      (obj.backgroundImage = message.backgroundImage
        ? LayoutData_BackgroundImage.toJSON(message.backgroundImage)
        : undefined);
    message.borderColor !== undefined && (obj.borderColor = message.borderColor);
    message.borderWidth !== undefined && (obj.borderWidth = message.borderWidth);
    message.borderRadius !== undefined && (obj.borderRadius = message.borderRadius);
    message.backdropColor !== undefined && (obj.backdropColor = message.backdropColor);
    message.backdropImage !== undefined &&
      (obj.backdropImage = message.backdropImage
        ? LayoutData_BackgroundImage.toJSON(message.backdropImage)
        : undefined);
    message.backdropPaddingTop !== undefined &&
      (obj.backdropPaddingTop = message.backdropPaddingTop);
    message.backdropPaddingBottom !== undefined &&
      (obj.backdropPaddingBottom = message.backdropPaddingBottom);
    message.gap !== undefined && (obj.gap = message.gap);
    if (message.cellPadding) {
      obj.cellPadding = message.cellPadding.map(e => Math.round(e));
    } else {
      obj.cellPadding = [];
    }
    message.cellVerticalAlignment !== undefined &&
      (obj.cellVerticalAlignment = layoutData_VerticalAlignment_AlignmentToJSON(
        message.cellVerticalAlignment
      ));
    message.responsivenessBehaviour !== undefined &&
      (obj.responsivenessBehaviour = layoutData_ResponsivenessBehaviourToJSON(
        message.responsivenessBehaviour
      ));
    message.responsivenessBreakpoint !== undefined &&
      (obj.responsivenessBreakpoint = message.responsivenessBreakpoint);
    return obj;
  },
};

const baseLayoutData_VerticalAlignment: object = {};

export const LayoutData_VerticalAlignment = {
  fromJSON(_: any): LayoutData_VerticalAlignment {
    const message = { ...baseLayoutData_VerticalAlignment } as LayoutData_VerticalAlignment;
    return message;
  },

  toJSON(_: LayoutData_VerticalAlignment): unknown {
    const obj: any = {};
    return obj;
  },
};

const baseLayoutData_ImageScaling: object = {};

export const LayoutData_ImageScaling = {
  fromJSON(_: any): LayoutData_ImageScaling {
    const message = { ...baseLayoutData_ImageScaling } as LayoutData_ImageScaling;
    return message;
  },

  toJSON(_: LayoutData_ImageScaling): unknown {
    const obj: any = {};
    return obj;
  },
};

const baseLayoutData_BackgroundImage: object = {
  scaling: LayoutData_ImageScaling_Scaling.AUTO,
  position: LayoutData_ImagePosition.CENTER,
};

export const LayoutData_BackgroundImage = {
  fromJSON(object: any): LayoutData_BackgroundImage {
    const message = { ...baseLayoutData_BackgroundImage } as LayoutData_BackgroundImage;
    message.media =
      object.media !== undefined && object.media !== null
        ? Media.fromJSON(object.media)
        : undefined;
    message.opacity =
      object.opacity !== undefined && object.opacity !== null ? Number(object.opacity) : undefined;
    message.scaling =
      object.scaling !== undefined && object.scaling !== null
        ? layoutData_ImageScaling_ScalingFromJSON(object.scaling)
        : LayoutData_ImageScaling_Scaling.AUTO;
    message.position =
      object.position !== undefined && object.position !== null
        ? layoutData_ImagePositionFromJSON(object.position)
        : LayoutData_ImagePosition.CENTER;
    return message;
  },

  toJSON(message: LayoutData_BackgroundImage): unknown {
    const obj: any = {};
    message.media !== undefined &&
      (obj.media = message.media ? Media.toJSON(message.media) : undefined);
    message.opacity !== undefined && (obj.opacity = message.opacity);
    message.scaling !== undefined &&
      (obj.scaling = layoutData_ImageScaling_ScalingToJSON(message.scaling));
    message.position !== undefined &&
      (obj.position = layoutData_ImagePositionToJSON(message.position));
    return obj;
  },
};
